import React, { useEffect, useState } from "react"
import { Box, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from "@mui/material"
import { AutocompleteAjaxSeeker, IconSeeker, NumberFieldSeeker } from "@ui-seeker/core"
import { useValidatorSeeker } from "@app"
import { object, string } from "yup"
import { BACKEND_APIS } from "@constants"
import { autoEuropeDrawerData, autoEuropeDrawerErrors } from "Pages/AutoEuropeTool/Page/@signals"
import ProviderOverrideModal from "Pages/AutoEuropeTool/Page/@components/ProviderOverrideModal"
import { dsColorNew } from "@palette"

const labelsFormatted = {
	0: { icon: "money-bills", label: "Currency", color: dsColorNew.successText },
	1: { icon: "percentage", label: "Percentage", color: "none" },
}

export default function AutoEuropeUndercut() {
	const initial = autoEuropeDrawerData.peek().undercut
	const [undercutValue, setUndercutValue] = useState(initial?.value ?? null)
	const [undercutType, setUndercutType] = useState(initial?.type ?? 1)
	const [undercutDirection, setUndercutDirection] = useState(initial?.direction ?? 0)
	const [modalOpen, setModalOpen] = useState(false)

	const isPercentage = undercutType === 1

	const schema = isPercentage
		? object({ undercutValue: string().required("Undercut is required").test("range", "Must be between 0.1 and 100", v => v !== null && v !== "" && Number(v) >= 0.1 && Number(v) <= 100) })
		: object({ undercutValue: string().required("Undercut is required").test("range", "Must be between 0.1 and 500", v => v !== null && v !== "" && Number(v) >= 0.1 && Number(v) <= 500) })

	const { errors, isValid } = useValidatorSeeker({ undercutValue }, schema)

	useEffect(() => {
		autoEuropeDrawerErrors.value = { ...autoEuropeDrawerErrors.value, undercutValue: isValid }
	}, [JSON.stringify(errors)])

	const updateSignal = (valueOverride, typeOverride, directionOverride) => {
		const current = autoEuropeDrawerData.value.undercut
		autoEuropeDrawerData.value = {
			...autoEuropeDrawerData.value,
			undercut: {
				value: valueOverride !== undefined ? valueOverride : undercutValue,
				type: typeOverride !== undefined ? typeOverride : undercutType,
				direction: directionOverride !== undefined ? directionOverride : undercutDirection,
				providers: current?.providers ?? [],
			},
		}
	}

	const handleValueChange = (e) => {
		setUndercutValue(e.target.value)
		updateSignal(e.target.value, undefined, undefined)
	}

	const handleTypeChange = (e) => {
		setUndercutType(e.target.value)
		updateSignal(undefined, e.target.value, undefined)
	}

	const handleDirectionChange = (e) => {
		setUndercutDirection(e.target.value)
		updateSignal(undefined, undefined, e.target.value)
	}

	const handleProvidersChange = (providers) => {
		autoEuropeDrawerData.value = {
			...autoEuropeDrawerData.value,
			undercut: { ...autoEuropeDrawerData.value.undercut, providers },
		}
	}

	const providers = autoEuropeDrawerData.value.undercut?.providers ?? []

	return (
		<Grid container spacing={2} alignItems="flex-start">
			<Grid item xs={5}>
				<NumberFieldSeeker
					label="Undercut"
					placeholder="0"
					value={undercutValue ?? ""}
					onChange={handleValueChange}
					error={errors?.undercutValue}
				/>
			</Grid>
			<Grid item xs={3}>
				<AutocompleteAjaxSeeker
					filterSelectedOptions={false}
					disableClearable
					urlAjax={`${BACKEND_APIS.MASTER_DATA}/all/options/comparator_unit`}
					label="Select unit"
					inputProps={{
						startAdornment: <IconSeeker
							icon={labelsFormatted[undercutType]?.icon}
							sx={{ color: labelsFormatted[undercutType]?.color, ml: 1, mr: .5 }}
						/>
					}}
					onAjax={options => options.map(({ value }) => ({
						value,
						label: labelsFormatted[value].label,
					}))}
					formatOptionLabel={({ value }) => (
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
							<IconSeeker icon={labelsFormatted[value].icon} sx={{ color: labelsFormatted[value].color }}/>
							<Typography>{labelsFormatted[value].label}</Typography>
						</Box>
					)}
					placeholder="Unit"
					value={undercutType}
					onChange={handleTypeChange}
				/>
			</Grid>
			<Grid item xs={3}>
				<FormControl size="small" fullWidth>
					<InputLabel>Direction</InputLabel>
					<Select label="Direction" value={undercutDirection} onChange={handleDirectionChange}>
						<MenuItem value={0}><Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}><IconSeeker icon="arrow-down" sx={{ fontSize: 11 }}/>Down</Box></MenuItem>
						<MenuItem value={1}><Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}><IconSeeker icon="arrow-up" sx={{ fontSize: 11 }}/>Up</Box></MenuItem>
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={1} sx={{ display: 'flex', alignItems: 'flex-start', pt: '20px !important' }}>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Tooltip title="Provider overrides">
						<IconButton size="small" onClick={() => setModalOpen(true)}>
							<IconSeeker icon="plus" fontSize="small"/>
						</IconButton>
					</Tooltip>
					{providers.length > 0 && (
						<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.appBlue }}>
							{providers.length}
						</Typography>
					)}
				</Box>
			</Grid>
			<ProviderOverrideModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				title="Undercut — Provider overrides"
				overrides={providers}
				onChange={handleProvidersChange}
				isUndercut
			/>
		</Grid>
	)
}